import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import PasswordInput from '../Enter/fields/PasswordInput';
import SubmitButton from '../Enter/fields/SubmitButton';
import { validateLength } from '../../utils/validation';
import { sendPermTokenRequest, sendChangePasswordRequest } from '../../utils/api/accountApi';
import { genericErrorMessage } from '../../utils/lang/commonErrors';
import { isLoggedIn } from '../../utils/auth';
import useQuery from '../../utils/useQuery';
import styles from './ChangePasswordForm.scss';

const ChangePasswordForm = () => {
  const history = useHistory();
  const intl = useIntl();
  const { perm_token: permanentToken } = useQuery();

  const [oldPassValue, setOldPassValue] = useState('');
  const onOldPassChange = (e) => setOldPassValue(e.target.value);

  const [newPassValue, setNewPassValue] = useState('');
  const onNewPassChange = (e) => setNewPassValue(e.target.value);

  const [isLoading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (permanentToken) {
      setLoading(true);
      sendPermTokenRequest(permanentToken).then(
        () => setLoading(false),
        (error) => {
          console.error(error);
          history.push('/enter');
        },
      );
    } else if (!isLoggedIn()) {
      history.push('/enter');
    }
  }, []);

  const onSubmit = () => {
    setLoading(true);
    const oldPassError = validateLength(oldPassValue, 8, 21);
    const newPassError = validateLength(newPassValue, 8, 21);

    if (oldPassError || newPassError) {
      setFormErrors({
        oldPass: oldPassError,
        newPass: newPassError,
      });
      setLoading(false);
    } else {
      sendChangePasswordRequest(oldPassValue, newPassValue)
        .then(() => {
          history.push('/profile');
        })
        .catch((error) => {
          if (error.code === 3) {
            setFormErrors({
              oldPass: intl.formatMessage({
                id: 'changePassword.error.wrongCurrentPassword',
                defaultMessage: 'Wrong password',
                description:
                  'Error shown to the user, when they inputted wrong password into the `current password` field',
              }),
            });
          } else {
            setFormErrors({
              oldPass: intl.formatMessage(genericErrorMessage),
            });
          }

          setLoading(false);
        });
    }
  };

  return (
    <form className={styles.form}>
      <h2 className={styles.title}>
        <FormattedMessage
          id="changePassword.title"
          defaultMessage="Change password"
          description="Title of the change current password page"
        />
      </h2>

      <PasswordInput
        passValue={oldPassValue}
        error={formErrors.oldPass}
        label={intl.formatMessage({
          id: 'changePassword.currentPasswordLabel',
          defaultMessage: 'Current password',
          description: 'Label of the field, where the user needs to input the current password to their account',
        })}
        onChange={onOldPassChange}
      />

      <PasswordInput
        passValue={newPassValue}
        error={formErrors.newPass}
        label={intl.formatMessage({
          id: 'changePassword.newPasswordLabel',
          defaultMessage: 'Set new password',
          description: 'Label of the field, where the user needs to input new password for their account',
        })}
        onChange={onNewPassChange}
      />

      <SubmitButton
        isLoading={isLoading}
        text={intl.formatMessage({
          id: 'changePassword.submitButton',
          defaultMessage: 'Update password',
          description:
            'Submit button of the change password page, on successful submit, the newly inputted password will be set for the account',
        })}
        onSubmit={onSubmit}
      />
    </form>
  );
};

export default ChangePasswordForm;
